import LinearProgress from "@material-ui/core/LinearProgress";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles/colorManipulator";
import TableCell from "@material-ui/core/TableCell";
import MuiTableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Tooltip from "@material-ui/core/Tooltip";
import classNames from "classnames";
import React from "react";
import { TableBodyColumnSchema, TableHeaderFooterColumn } from "../models";
import { TableSortingProps } from "./Table";

const styles = (theme: Theme) =>
  createStyles({
    tableHeadRow: {
      height: 48,
    },
    tableHeadCell: {
      borderBottom: 0,
      // Background color must be defined on cell instead of row, so that background-color remains fixed with
      // position: sticky
      backgroundColor: darken(theme.palette.background.default, 0.05),
    },
    tableHeadCellBottomBorder: {
      borderBottom: `solid 1px ${theme.palette.divider}`,
    },
    tableHeadRowStickyCell: {
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
    tableHeadBorderRow: {
      height: 1,
    },
    tableHeadBorderCell: {
      position: "sticky",
      top: 48,
      zIndex: 1,
      border: 0,
      padding: 0,
      "&:last-child": {
        padding: 0,
      },
    },
    tableHeaderBorder: {
      margin: 0,
      border: 0,
      height: 1,
      backgroundColor: theme.palette.divider,
    },
    tableHeadLoaderContainer: {
      position: "relative",
    },
    tableHeadLoader: {
      position: "absolute",
      top: 0,
      width: "100%",
    },
  });

interface TableHeadProps extends WithStyles<typeof styles> {
  id?: string;
  headerRow?: TableHeaderFooterColumn[];
  columnSchemas: TableBodyColumnSchema[];
  sorting?: TableSortingProps;
  sticky?: boolean;
  isLoading: boolean;
}

export const TableHeadComponent = (props: TableHeadProps) => {
  const { id, columnSchemas, headerRow, sorting, sticky, isLoading, classes } = props;

  let handleSortLabelClick: ((sortPropertyName: string) => () => void) | undefined;

  if (sorting && sorting.onSortChange) {
    const { sortPropertyName: prevSortPropertyName, sortDirection: prevSortDirection, onSortChange } = sorting;
    handleSortLabelClick = (sortPropertyName: string) => () => {
      if (sortPropertyName) {
        const sortDirection =
          sortPropertyName === prevSortPropertyName ? (prevSortDirection === "asc" ? "desc" : "asc") : "asc";
        onSortChange(sortPropertyName, sortDirection);
      }
    };
  }

  const tableHeadCellClassName = classNames(classes.tableHeadCell, sticky ? classes.tableHeadRowStickyCell : undefined);

  return (
    <MuiTableHead id={id}>
      {headerRow ? (
        <TableRow className={classes.tableHeadRow}>
          {headerRow.map((headerRowColumn, index) => (
            <TableCell
              key={`hrc-${index}`}
              padding={headerRowColumn.padding}
              size={headerRowColumn.size}
              align={headerRowColumn.align}
              colSpan={headerRowColumn.colSpan}
              className={classNames(
                classes.tableHeadCell,
                headerRowColumn.content && classes.tableHeadCellBottomBorder,
              )}
              style={{ display: headerRowColumn.hidden ? "none" : undefined }}
            >
              {headerRowColumn.content}
            </TableCell>
          ))}
        </TableRow>
      ) : null}
      <TableRow className={classes.tableHeadRow}>
        {columnSchemas.map((columnSchema, index) => (
          <TableCell
            key={`hc-${index}`}
            padding={columnSchema.padding}
            size={columnSchema.size}
            align={columnSchema.align}
            className={tableHeadCellClassName}
            style={{ width: columnSchema.width, display: columnSchema.hidden ? "none" : undefined }}
          >
            {columnSchema.label && sorting && handleSortLabelClick && !columnSchema.disableSort ? (
              <Tooltip title="Sort" enterDelay={300}>
                <TableSortLabel
                  active={sorting.sortPropertyName === columnSchema.id}
                  direction={sorting.sortDirection}
                  onClick={handleSortLabelClick(columnSchema.id)}
                >
                  {columnSchema.label}
                </TableSortLabel>
              </Tooltip>
            ) : (
              columnSchema.label
            )}
          </TableCell>
        ))}
      </TableRow>
      <TableRow className={classes.tableHeadBorderRow}>
        <TableCell colSpan={columnSchemas.length} className={classes.tableHeadBorderCell}>
          <div className={classes.tableHeadLoaderContainer}>
            <hr className={classes.tableHeaderBorder} />
            {isLoading && <LinearProgress variant="indeterminate" className={classes.tableHeadLoader} />}
          </div>
        </TableCell>
      </TableRow>
    </MuiTableHead>
  );
};

export const TableHead = withStyles(styles)(TableHeadComponent);
